import React from 'react';
import { toast } from 'react-toastify';
import {
  listTransactions, getTotals, createTransaction,
  darBaixa, cancelarTransaction, listPersons, listAccounts,
  listCostCenters, listBankAccounts,
  createCostCenter, createPerson, createAccount
} from '../../../../api/openFinanceTransactions';
import {
  C, brl, fmtDate, StatusBadge, KPICard, Overlay, Btn, Field,
  AccountSelect, PAYMENT_METHODS, periodRange,
} from '../components/shared';
import { _Input } from '../../../../_components/Input';
import { getClients } from '../../../../api/clients';
import { getPaymentCategory, getPaymentCostCenter, filterTransaction, ConfirmTransaction, CancelTransaction } from '../../../../api/financial';

function _normalizeDjangoItem(t) {
  const isOverdue = t.status === 1 && new Date(t.due_date) < new Date(today());
  let statusStr = 'pending';
  if (t.status === 2) statusStr = 'paid';
  else if (t.status === 3) statusStr = 'canceled';
  else if (isOverdue) statusStr = 'overdue';

  return {
    _source: 'django',
    id: t.id,
    amount: (t.value || 0) + (t.tax || 0) + (t.penalty || 0),
    effective_amount: t.paid_value || ((t.value || 0) + (t.tax || 0) + (t.penalty || 0)),
    status: statusStr,
    person: { name: t.recipient ? t.recipient.name : 'Não Informado' },
    chart_of_account: { name: t.category ? t.category.name : 'Sem Categoria' },
    cost_center: { name: t.cost_center ? t.cost_center.name : 'Sem Centro de Custo' },
    payment_method: t.payment_method ? t.payment_method.name : '-',
    date_due: t.due_date,
    description: t.description,
    installment_total: 1,
    installment_current: 1
  };
}

const today = () => new Date().toISOString().split('T')[0];

const EMPTY_FORM = {
  transaction_type: 'C',
  description: '', amount: '', date_due: today(), date_accrual: today(),
  person_id: '', chart_of_account_id: '', cost_center_id: '',
  payment_method: 'PIX', bank_account_id: '', date_payment: '',
  interest_amount: 0, fine_amount: 0, discount_amount: 0, fee_amount: 0,
  notes: '', installments: 1, periodicity: 'mensal',
};

const EMPTY_BAIXA = {
  date_payment: today(), bank_account_id: '', payment_method: 'PIX',
  interest_amount: 0, fine_amount: 0, discount_amount: 0, fee_amount: 0,
};

export default function PaymentsIn() {
  const [rows, setRows] = React.useState([]);
  const [totals, setTotals] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [period, setPeriod] = React.useState('month');
  const [statusF, setStatusF] = React.useState('todos');
  const [persons, setPersons] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const [centers, setCenters] = React.useState([]);
  const [banks, setBanks] = React.useState([]);
  const [showCreate, setCreate] = React.useState(false);
  const [showBaixa, setBaixa] = React.useState(false);
  const [showCancel, setCancel] = React.useState(false);
  const [showView, setView] = React.useState(false);
  const [current, setCurrent] = React.useState(null);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [baixaForm, setBaixaForm] = React.useState(EMPTY_BAIXA);
  const [saving, setSaving] = React.useState(false);

  const pr = periodRange(period);

  async function load() {
    setLoading(true);
    try {
      const djangoStatusMap = {
        'pending': 1,
        'paid': 2,
        'canceled': 3,
        // overdue handled by clientside filtering or sending status=1
      };

      const legacyParams = {
        type: 1, // 1 for PaymentsIn
        end_count: 1000, // Django usa end_count, não limit
        start_date: pr.date_from,
        end_date: pr.date_to,
        search: search || undefined
      };

      if (statusF && statusF !== 'todos' && statusF !== 'overdue') {
        legacyParams.status = djangoStatusMap[statusF];
      } else if (statusF === 'overdue') {
        legacyParams.status = 1; // pending (we filter overdue clientside)
      }

      const [res, tot, legacyRes] = await Promise.allSettled([
        listTransactions({ transaction_type: 'C', status: statusF, ...pr, per_page: 100 }),
        getTotals(pr),
        filterTransaction(legacyParams)
      ]);

      const openFinanceTxs = (res.status === 'fulfilled' && res.value && res.value.transactions) ? res.value.transactions : [];
      let legacyTxs = [];
      if (legacyRes.status === 'fulfilled' && legacyRes.value) {
        // O interceptor do axios já faz response.data, então legacyRes.value é o array diretamente
        const rawLegacy = Array.isArray(legacyRes.value)
          ? legacyRes.value
          : (legacyRes.value.results || legacyRes.value.data || []);
        legacyTxs = rawLegacy.map(_normalizeDjangoItem);
        if (statusF === 'overdue') {
          legacyTxs = legacyTxs.filter(t => t.status === 'overdue');
        } else if (statusF === 'pending') {
          legacyTxs = legacyTxs.filter(t => t.status === 'pending');
        }
      }

      const combined = [...openFinanceTxs, ...legacyTxs];
      // Sort by due date
      combined.sort((a, b) => new Date(a.date_due) - new Date(b.date_due));

      // Calculate totals for legacy (since getTotals only returns OF totals)
      const payInTot = (tot.status === 'fulfilled' && tot.value && tot.value.pay_in) ? tot.value.pay_in : { received_liquid: 0, to_receive: 0, inadimplente: 0, adjustments: { interest: 0, fine: 0, discount: 0, fee: 0 } };

      let extraToReceive = 0, extraReceived = 0, extraInadimplente = 0;
      legacyTxs.forEach(t => {
        if (t.status === 'pending') extraToReceive += t.amount;
        if (t.status === 'paid') extraReceived += t.effective_amount;
        if (t.status === 'overdue') extraInadimplente += t.amount;
      });

      payInTot.received_liquid += extraReceived;
      payInTot.to_receive += extraToReceive;
      payInTot.inadimplente += extraInadimplente;

      setRows(combined);
      setTotals(payInTot);
    } catch (err) { toast.error('Erro ao carregar dados'); }
    finally { setLoading(false); }
  }

  async function loadSupport() {
    try {
      const [p, a, cc, b] = await Promise.all([
        listPersons({ tipo: 'cliente' }).catch(() => []),
        listAccounts().catch(() => []),
        listCostCenters().catch(() => []),
        listBankAccounts().catch(() => [])
      ]);

      const [legP, legA, legCC] = await Promise.all([
        getClients({ limit: 1000 }).catch(() => ({ data: [] })),
        getPaymentCategory({ limit: 1000 }).catch(() => ({ data: [] })),
        getPaymentCostCenter({ limit: 1000 }).catch(() => ({ data: [] })),
      ]);

      const mapL = (list, isPerson = false) => {
        const arr = Array.isArray(list) ? list : ((list && list.results) || []);
        return arr.map(x => ({
          id: String(x.id),
          name: isPerson ? (x.name || x.fantasy_name || x.corporate_name || 'Sem Nome') : x.name,
          _isLegacy: true
        }));
      };

      setPersons([...(p || []), ...mapL(legP, true)]);
      setAccounts([...(a || []), ...mapL(legA)]);
      setCenters([...(cc || []), ...mapL(legCC)]);
      setBanks(b || []);
    } catch (e) { /* ignore support load errors */ }
  }

  React.useEffect(() => { load(); }, [period, statusF]);
  React.useEffect(() => { loadSupport(); }, []);

  const onForm = e => {
    const target = e.target || e;
    const { name, value } = target;
    setForm(f => ({ ...f, [name]: value }));
  };
  const onBaixa = e => {
    const target = e.target || e;
    const { name, value } = target;
    setBaixaForm(f => ({ ...f, [name]: value }));
  };

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      let finalCostCenterId = form.cost_center_id;
      const selectedCC = centers.find(c => c.id === form.cost_center_id);
      if (selectedCC && selectedCC._isLegacy) {
        const newCC = await createCostCenter({ name: selectedCC.name });
        finalCostCenterId = newCC.id;
      }

      let finalPersonId = form.person_id;
      const selectedPerson = persons.find(p => p.id === form.person_id);
      if (selectedPerson && selectedPerson._isLegacy) {
        const newPerson = await createPerson({ name: selectedPerson.name, category: 'Cliente' });
        finalPersonId = newPerson.id;
      }

      let finalAccountId = form.chart_of_account_id;
      const selectedAcc = accounts.find(a => a.id === form.chart_of_account_id);
      if (selectedAcc && selectedAcc._isLegacy) {
        const newAcc = await createAccount({ name: selectedAcc.name, type: 'Receita' });
        finalAccountId = newAcc.id;
      }

      const payload = {
        ...form,
        person_id: finalPersonId || null,
        chart_of_account_id: finalAccountId || null,
        cost_center_id: finalCostCenterId || null,
        date_due: form.date_due || null,
        date_payment: form.date_payment || null,
        amount: parseFloat(form.amount) || 0,
        interest_amount: parseFloat(form.interest_amount) || 0,
        fine_amount: parseFloat(form.fine_amount) || 0,
        discount_amount: parseFloat(form.discount_amount) || 0,
        fee_amount: parseFloat(form.fee_amount) || 0,
        ...(parseInt(form.installments) > 1 ? {
          installment: { total_installments: parseInt(form.installments), periodicity: form.periodicity, first_date: form.date_due }
        } : {}),
      };
      await createTransaction(payload);
      toast.success('Conta a receber criada!');
      setCreate(false); setForm(EMPTY_FORM); load();
    } catch (e) { toast.error('Erro ao criar conta'); }
    finally { setSaving(false); }
  }

  async function handleBaixa(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (current._source === 'django') {
        // Roteamento para o Django legado
        const paidValue = current.amount
          + parseFloat(baixaForm.interest_amount || 0)
          + parseFloat(baixaForm.fine_amount || 0)
          - parseFloat(baixaForm.discount_amount || 0);
        await ConfirmTransaction(current.id, {
          value:    paidValue,
          discount: parseFloat(baixaForm.discount_amount || 0),
          tax:      parseFloat(baixaForm.interest_amount || 0),
          penalty:  parseFloat(baixaForm.fine_amount || 0),
          due_date: baixaForm.date_payment || null,
        });
      } else {
        // Roteamento para a open-finance-api
        await darBaixa(current.id, {
          ...baixaForm,
          date_payment:    baixaForm.date_payment || null,
          interest_amount: parseFloat(baixaForm.interest_amount) || 0,
          fine_amount:     parseFloat(baixaForm.fine_amount) || 0,
          discount_amount: parseFloat(baixaForm.discount_amount) || 0,
          fee_amount:      parseFloat(baixaForm.fee_amount) || 0,
        });
      }
      toast.success('Recebimento confirmado!');
      setBaixa(false); load();
    } catch (err) { toast.error((err && err.response && err.response.data && err.response.data.detail) || 'Erro ao confirmar recebimento'); }
    finally { setSaving(false); }
  }

  async function handleCancel() {
    setSaving(true);
    try {
      if (current._source === 'django') {
        // Roteamento para o Django legado
        await CancelTransaction(current.id);
      } else {
        // Roteamento para a open-finance-api
        await cancelarTransaction(current.id, { motivo: 'Cancelado pelo usuário' });
      }
      toast.success('Conta cancelada!');
      setCancel(false); load();
    } catch (e) { toast.error('Erro ao cancelar'); }
    finally { setSaving(false); }
  }

  const filtered = rows.filter(r =>
    !search || r.description && r.description.toLowerCase().includes(search.toLowerCase())
  );

  const adj = totals.adjustments || {};
  const adjTotal = (adj.interest || 0) + (adj.fine || 0) - (adj.discount || 0) - (adj.fee || 0);

  return (
    <div style={{ paddingBottom: 32 }}>
      {/* KPI Row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <KPICard icon="arrow-down" label="Recebido (líquido)" value={brl(totals.received_liquid)} valueColor={C.green} iconBg={C.greenBg} iconColor={C.green} sub={`${rows.filter(r => r.status === 'paid').length} lançamentos`} />
        <KPICard icon="clock" label="A receber" value={brl(totals.to_receive)} valueColor={C.amber} iconBg={C.amberBg} iconColor={C.amber} sub={`${rows.filter(r => r.status === 'pending').length} pendentes`} />
        <KPICard icon="exclamation-circle" label="Inadimplente" value={brl(totals.inadimplente)} valueColor={C.red} iconBg={C.redBg} iconColor={C.red} sub={`${rows.filter(r => r.status === 'overdue').length} em atraso`} />
        <KPICard icon="sliders-h" label="Ajustes (juros/desc.)" value={adjTotal >= 0 ? `+ ${brl(adjTotal)}` : `- ${brl(Math.abs(adjTotal))}`} valueColor={adjTotal >= 0 ? C.green : C.red} iconBg={C.purpleBg} iconColor={C.purple} />
      </div>

      {/* Toolbar */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 20px', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={{ padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, cursor: 'pointer' }}>
          <option value="month">Este mês</option>
          <option value="last_month">Mês passado</option>
          <option value="next30">Próximos 30 dias</option>
          <option value="all">Todos</option>
        </select>
        <select value={statusF} onChange={e => setStatusF(e.target.value)} style={{ padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, cursor: 'pointer' }}>
          <option value="todos">Todos os status</option>
          <option value="pending">Pendentes</option>
          <option value="paid">Recebidos</option>
          <option value="overdue">Inadimplentes</option>
          <option value="canceled">Cancelados</option>
        </select>
        <div style={{ flex: 1, minWidth: 180, position: 'relative' }}>
          <i className="fas fa-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: C.soft, fontSize: 12 }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por descrição..."
            style={{ width: '100%', padding: '7px 10px 7px 32px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <Btn onClick={() => { setForm(EMPTY_FORM); setCreate(true); }} color={C.green}>
          <i className="fas fa-plus" style={{ fontSize: 11 }} /> Nova Conta a Receber
        </Btn>
      </div>

      {/* Table */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: `1px solid ${C.border}` }}>
              {['Descrição', 'Cliente', 'Vencimento', 'Valor', 'Status', 'Ações'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: C.soft }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: C.soft }}>
                <i className="fas fa-spinner fa-spin" /> Carregando...
              </td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: C.soft }}>
                Nenhum lançamento encontrado.
              </td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={r.id} style={{ borderBottom: `1px solid ${C.border}`, background: i % 2 === 0 ? '#fff' : '#FAFBFC', transition: 'background .15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F0FFF4'}
                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#FAFBFC'}>
                <td style={{ padding: '10px 16px', fontWeight: 600, color: C.text }}>
                  <div>{r.description}</div>
                  {r.installment_total > 1 && <div style={{ fontSize: 10.5, color: C.soft }}>{r.installment_current || 1}/{r.installment_total} parcelas</div>}
                </td>
                <td style={{ padding: '10px 16px', color: C.soft }}>{(r.person && r.person.name) || '—'}</td>
                <td style={{ padding: '10px 16px' }}>
                  <span style={{ color: r.status === 'overdue' ? C.red : C.text, fontWeight: r.status === 'overdue' ? 700 : 400 }}>
                    {fmtDate(r.date_due)}
                  </span>
                </td>
                <td style={{ padding: '10px 16px', fontWeight: 700, color: C.green }}>{brl(r.effective_amount !== undefined ? r.effective_amount : r.amount)}</td>
                <td style={{ padding: '10px 16px' }}><StatusBadge status={r.status} /></td>
                <td style={{ padding: '10px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(r.status === 'pending' || r.status === 'overdue') && (
                      <Btn small color={C.green} onClick={() => { setCurrent(r); setBaixaForm({ ...EMPTY_BAIXA }); setBaixa(true); }}>
                        <i className="fas fa-check" /> Receber
                      </Btn>
                    )}
                    <Btn small outline color={C.blue} onClick={() => { setCurrent(r); setView(true); }}>
                      <i className="fas fa-eye" />
                    </Btn>
                    {r.status !== 'canceled' && r.status !== 'paid' && (
                      <Btn small outline color={C.red} onClick={() => { setCurrent(r); setCancel(true); }}>
                        <i className="fas fa-times" />
                      </Btn>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Overlay show={showCreate} onClose={() => setCreate(false)} title="Nova Conta a Receber" width={760}
        footer={<React.Fragment><Btn outline color={C.soft} onClick={() => setCreate(false)}>Cancelar</Btn><Btn color={C.green} onClick={handleCreate} disabled={saving}>{saving ? 'Salvando...' : 'Salvar Conta'}</Btn></React.Fragment>}>
        <form onSubmit={handleCreate}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <_Input label="Descrição*" name="description" value={form.description} onChange={onForm} placeholder="Ex: Mensalidade, Venda X..." />
            </div>
            <_Input label="Valor (R$)*" name="amount" type="currency" value={form.amount} onChange={onForm} placeholder="0,00" />
            <_Input label="Vencimento*" name="date_due" type="date" value={form.date_due} onChange={onForm} />
            <_Input label="Cliente" name="person_id" type="select" value={form.person_id} onChange={onForm} options={[{ label: '— Selecione —', value: '' }, ...persons.map(p => ({ label: p.name, value: p.id }))]} />
            <_Input label="Forma de Recebimento" name="payment_method" type="select" value={form.payment_method} onChange={onForm} options={PAYMENT_METHODS.map(m => ({ label: m, value: m }))} />
            <Field label="Plano de Contas">
              <AccountSelect name="chart_of_account_id" value={form.chart_of_account_id} onChange={onForm} accounts={accounts} />
            </Field>
            <_Input label="Centro de Custo" name="cost_center_id" type="select" value={form.cost_center_id} onChange={onForm} options={[{ label: '— Selecione —', value: '' }, ...centers.map(c => ({ label: c.name, value: c.id }))]} />
            <_Input label="Parcelas" name="installments" type="number" value={form.installments} onChange={onForm} placeholder="1" />
            {parseInt(form.installments) > 1 && (
              <_Input label="Periodicidade" name="periodicity" type="select" value={form.periodicity} onChange={onForm} options={[{ label: 'Mensal', value: 'mensal' }, { label: 'Semanal', value: 'semanal' }, { label: 'Quinzenal', value: 'quinzenal' }, { label: 'Bimestral', value: 'bimestral' }]} />
            )}
            <div style={{ gridColumn: '1/-1' }}>
              <Field label="Observações">
                <textarea name="notes" value={form.notes} onChange={onForm} rows={3} placeholder="Informações adicionais..."
                  style={{ width: '100%', padding: '7px 10px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 13, color: C.text, resize: 'vertical', boxSizing: 'border-box' }} />
              </Field>
            </div>
          </div>
        </form>
      </Overlay>

      {/* Baixa Modal */}
      <Overlay show={showBaixa} onClose={() => setBaixa(false)} title={`Confirmar Recebimento — ${(current && current.description) || ''}`} width={520}
        footer={<React.Fragment><Btn outline color={C.soft} onClick={() => setBaixa(false)}>Cancelar</Btn><Btn color={C.green} onClick={handleBaixa} disabled={saving}>{saving ? 'Salvando...' : 'Confirmar Recebimento'}</Btn></React.Fragment>}>
        {current && (
          <div style={{ display: 'grid', gap: 12 }}>
            <div style={{ background: C.greenBg, borderRadius: 8, padding: '10px 16px', fontSize: 13 }}>
              Valor original: <strong style={{ color: C.green }}>{brl(current.amount)}</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <_Input label="Data do Recebimento*" name="date_payment" type="date" value={baixaForm.date_payment} onChange={onBaixa} />
              <_Input label="Forma de Recebimento" name="payment_method" type="select" value={baixaForm.payment_method} onChange={onBaixa} options={PAYMENT_METHODS.map(m => ({ label: m, value: m }))} />
              <_Input label="Conta Bancária" name="bank_account_id" type="select" value={baixaForm.bank_account_id} onChange={onBaixa} options={[{ label: '— Selecione —', value: '' }, ...banks.map(b => ({ label: b.name, value: b.id }))]} />
              <_Input label="Juros (R$)" name="interest_amount" type="currency" value={baixaForm.interest_amount} onChange={onBaixa} />
              <_Input label="Multa (R$)" name="fine_amount" type="currency" value={baixaForm.fine_amount} onChange={onBaixa} />
              <_Input label="Desconto (R$)" name="discount_amount" type="currency" value={baixaForm.discount_amount} onChange={onBaixa} />
            </div>
          </div>
        )}
      </Overlay>

      {/* View Modal */}
      <Overlay show={showView} onClose={() => setView(false)} title="Detalhes do Lançamento" width={580}>
        {current && (
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, color: C.text }}>{current.description}</h4>
              <StatusBadge status={current.status} />
            </div>
            {[
              ['Valor', brl(current.effective_amount !== undefined ? current.effective_amount : current.amount)],
              ['Vencimento', fmtDate(current.date_due)],
              ['Recebimento', fmtDate(current.date_payment)],
              ['Forma de Recebimento', current.payment_method || '—'],
              ['Observações', current.notes || '—'],
            ].map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${C.border}`, paddingBottom: 8 }}>
                <span style={{ fontSize: 12.5, color: C.soft }}>{l}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{v}</span>
              </div>
            ))}
          </div>
        )}
      </Overlay>

      {/* Cancel Modal */}
      <Overlay show={showCancel} onClose={() => setCancel(false)} title="Cancelar Lançamento" width={440}
        footer={<React.Fragment><Btn outline color={C.soft} onClick={() => setCancel(false)}>Voltar</Btn><Btn color={C.red} onClick={handleCancel} disabled={saving}>{saving ? 'Cancelando...' : 'Confirmar Cancelamento'}</Btn></React.Fragment>}>
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: C.redBg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <i className="fas fa-exclamation-triangle" style={{ color: C.red, fontSize: 22 }} />
          </div>
          <p style={{ color: C.text, fontSize: 14 }}>
            Tem certeza que deseja cancelar <strong>{current && current.description}</strong>?<br />
            <span style={{ color: C.soft, fontSize: 12.5 }}>Essa ação não pode ser desfeita.</span>
          </p>
        </div>
      </Overlay>
    </div>
  );
}


// WEBPACK FOOTER //
// ./src/views/AppViews/Financial/PaymentsIn/index.jsx